import { type CollectionHoursDraft } from "../../components/CollectionHoursInput";
import { matchesDraftTemplate } from "../../lib/postingDraftStorage";

export const STEPS = ["Choose Type", "Fill Details", "Add Media", "Pricing / Terms", "Review", "Publish"];

// These post types require a cropped main photo before publication; RFQ photos remain optional.
export const IMAGE_TYPES = ["item", "service", "company-service", "job", "project"];

// Previous-work evidence is for providers and RFQ posters, not job openings.
export const PREVWORK_TEXT_TYPES = ["service", "company-service", "rfq"];

export const PREVWORK_IMAGE_TYPES = ["service", "company-service"];

// [posting.publication-access](../../../../docs/product/marketplace.md#posting-publication-access): temporarily keep every posting flow single-title, including queued drafts.
export const MULTI_TYPES: string[] = [];

export const JOB_WORK_ARRANGEMENTS = ["On-site", "Hybrid", "Remote"];

export const RENTAL_RATE_PERIODS = ["day", "week", "month"] as const;

export const DEFAULT_ITEM_SALE_UNIT = "piece";

export const DEFAULT_ITEM_SALE_QUANTITY = "1";

export const ITEMS_PER_DOZEN = 12;

export const PICKUP_LOCATION_REQUIRED_MESSAGE = "Enter the item location when Pickup is selected.";

export const QUICK_SUGGESTION_LIMIT = 3;

export const GENERAL_POST_TAGS = ["Customer service", "Communication", "Attention to detail", "Project management", "Operations"];

// Keep each post type's API route, capability, marketplace tab, and detail route together.
export const TYPE_MAP: Record<string, { endpoint: string; cap: string; tab: string; detailPath: string } | null> = {
  service: { endpoint: "service", cap: "offer_service", tab: "Services", detailPath: "/s" },
  "company-service": { endpoint: "service", cap: "offer_service", tab: "Services", detailPath: "/s" },
  company: { endpoint: "company", cap: "promote_company", tab: "Companies", detailPath: "/c" },
  job: { endpoint: "job", cap: "post_job", tab: "Jobs", detailPath: "/j" },
  project: { endpoint: "project", cap: "post_project", tab: "Project Bidding", detailPath: "/project" },
  rfq: { endpoint: "rfq", cap: "post_project", tab: "RFQs", detailPath: "/rfq" },
  item: { endpoint: "item", cap: "offer_service", tab: "Materials", detailPath: "/item" },
};

export const LOCATION_HISTORY_GROUPS: Record<string, "services" | "companies" | "jobs" | "projects" | "rfqs" | undefined> = {
  service: "services", "company-service": "services", company: "companies",
  job: "jobs", project: "projects", rfq: "rfqs",
};

// [posting.location-defaults](../../../../docs/product/marketplace.md#posting-location-defaults): owner history is newest first. Only the same kind's public location
// is reused; historical projects may contain private sites that are never defaults.
export function lastPostingLocation(type: string, posts: unknown): string {
  if (!Array.isArray(posts)) return "";
  const candidates: unknown[] = posts;
  for (const post of candidates) {
    if (typeof post !== "object" || post === null) continue;
    if ((type === "service" || type === "company-service") && ("company" in post && post.company === true) !== (type === "company-service")) continue;
    const detail = "detail" in post ? post.detail : null;
    const location = type === "project"
      ? typeof detail === "object" && detail !== null && "publicLocation" in detail ? detail.publicLocation : undefined
      : type === "item" || type === "rfq"
        ? "location" in post ? post.location : undefined
        : "city" in post ? post.city : undefined;
    if (typeof location === "string" && location.trim()) return location.trim();
  }
  return "";
}

// [#28] Posting a type the account can't yet post is an explicit role change, not
// a silent grant. This maps the capability to the human role it adds so we can ask
// the user to confirm before enabling it (KontrataPH is multi-role: one account can
// hold several, but each is opted into deliberately).
export const ROLE_ENABLE: Record<string, { role: string; blurb: string }> = {
  post_job: { role: "Hiring", blurb: "post jobs and hire workers" },
  post_project: { role: "Hiring", blurb: "post projects & RFQs and receive bids" },
  offer_service: { role: "Service Provider", blurb: "list services or items and receive client inquiries" },
  find_work: { role: "Find Work", blurb: "publish a résumé and apply for jobs" },
  promote_company: { role: "Business", blurb: "run a company profile" },
};

export const amountLabel: Record<string, string> = {
  job: "Monthly salary (PHP)", service: "Starting price (PHP)", project: "Budget (PHP)",
  "company-service": "Starting price (PHP)", resume: "Years of experience", company: "",
  item: "Price (PHP)",
};

export const fmtSize = (n: number) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export type ProjectRequirementKind = "requirements" | "licenses";

export type ProjectRequirementEntry = { value: string; type?: string };

export const newProjectRequirementEntry = (): ProjectRequirementEntry => ({ value: "", type: "" });

export const requirementValues = (entries: ProjectRequirementEntry[]) => entries.map((entry) => entry.value);

export const publishedRequirements = (entries: ProjectRequirementEntry[]) => entries
  .filter((entry) => entry.value.trim())
  .map((entry) => [entry.type?.trim(), entry.value.trim()].filter(Boolean).join(": "));

export const PROJECT_LICENSE_CHOICES = ["Business permit", "DTI registration", "SEC registration", "Professional license"];

export const PROJECT_REQUIREMENT_SUGGESTIONS: Record<string, string[]> = {
  experience: ["At least 1 year of relevant experience", "At least 2 years of relevant experience", "At least 3 years of relevant experience", "At least 5 years of relevant experience"],
  portfolio: ["At least 2 relevant portfolio projects", "At least 3 relevant portfolio projects", "At least 4 relevant portfolio projects"],
  location: ["Based in the project city", "Available to work on site", "Able to travel to the project location"],
  documents: ["Provide a company profile", "Provide a project methodology", "Provide proof of similar work"],
};

export const displayCityName = (name: string) => {
  const titled = name.replace(/\s*\(CAPITAL\)/giu, "").toLocaleLowerCase("en-PH").replace(/(^|[\s(-])\p{L}/gu, (letter) => letter.toLocaleUpperCase("en-PH"));
  return titled.startsWith("City Of ") ? `${titled.slice(8)} City` : titled;
};

export const PURCHASE_FACT_FORM = { purchaseInspectionSource: "", purchaseInspectionSchedule: "", purchasePropertySource: "", purchasePropertySchedule: "" };

export const EMPTY_POST_FORM = { ...PURCHASE_FACT_FORM, title: "", requestType: "", category: "", categoryOther: "", location: "", description: "", productInformation: "", benefits: "", previousWork: "", whatsIncluded: "", requirements: "", tags: "", workArrangement: "", amount: "", deadline: "", targetCompletionDate: "", negotiable: false, kind: "", itemType: "", mode: "", unit: "", stock: "", condition: "", businessOperatingStatus: "", pickupLocation: "", pickupUsesItemLocation: false, pickupHours: "", rateAmount: "", ratePeriod: "day", term: "Negotiable" };

export const EMPTY_COLLECTION_HOURS: CollectionHoursDraft = { days: "Every day", from: "", until: "", custom: false, text: "" };

// [posting.draft-recovery](../../../../docs/product/marketplace.md#posting-draft-recovery): posting drafts retain unfinished picker inputs in the existing
// string field; older readable schedules reopen as custom hours. API writes use
// collectionHoursText instead, so this local encoding never becomes public hours.
export function collectionHoursFromDraft(saved: string): CollectionHoursDraft {
  try {
    const parsed: unknown = JSON.parse(saved);
    if (matchesDraftTemplate(EMPTY_COLLECTION_HOURS, parsed)) return parsed;
  } catch { /* Older drafts contain a readable schedule instead of picker JSON. */ }
  return {
    ...EMPTY_COLLECTION_HOURS, custom: Boolean(saved), text: saved
  };
}
